'use client';
import { useCredits } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { ContainerStatus } from '@/app/types';
import { convertContainerToNodeData } from '@/app/utils';
import { useGetContainerList } from '../apis';
import { useFocusNodeById } from './use-canvas-control';

// 任务轮询间隔时间（毫秒）
const POLLING_INTERVAL = 5000;

// 默认延迟启动时间（毫秒）
const DEFAULT_POLLING_DELAY = 500;

// 轮询类型
type PollingType = 'startPolling' | undefined;

export type StartPollingFunction = (type?: PollingType, delay?: number) => void;

// 判断容器是否正在处理中
export const isContainerProcessing = (status?: ContainerStatus): boolean => {
  return status === 'created' || status === 'queueing' || status === 'processing';
};

/**
 * 任务轮询 Hook
 * 用于轮询获取容器节点状态，更新画布内容
 */
export const useTask = () => {
  const storeApi = useStoreApi();
  const focusNode = useFocusNodeById();
  const {
    setAgentEditInClipLoadingNodes,
    agentIsOpenEditInClipDialog,
    setAgentPollingState,
    clearAgentPollingTimer,
    setAgentLoadingNodeIds,
    batchUpdateAgentContainers,
    agentIsPolling,
  } = useStore((state) => ({
    setAgentEditInClipLoadingNodes: state.setAgentEditInClipLoadingNodes,
    agentIsOpenEditInClipDialog: state.agentIsOpenEditInClipDialog,
    setAgentPollingState: state.setAgentPollingState,
    clearAgentPollingTimer: state.clearAgentPollingTimer,
    setAgentLoadingNodeIds: state.setAgentLoadingNodeIds,
    batchUpdateAgentContainers: state.batchUpdateAgentContainers,
    agentIsPolling: state.agentIsPolling,
  }));
  const { updateCredits } = useCredits();
  // API Hook
  const { fetchContainerListByIds } = useGetContainerList();

  // 停止轮询
  const stopPolling = () => {
    clearAgentPollingTimer();
  };

  /**
   * 检查并更新容器状态
   *
   * @param type - 轮询类型，'startPolling' 表示初始轮询，会获取所有容器
   */
  const checkAndUpdateContainers = async (type?: PollingType) => {
    const agentCanvasDetail = storeApi.getState().agentCanvasDetail;
    const agentLoadingNodeIds = storeApi.getState().agentLoadingNodeIds;
    const agentIsPolling = storeApi.getState().agentIsPolling;

    // 检查是否应该停止轮询
    if (!agentCanvasDetail?.id || !agentIsPolling || (type !== 'startPolling' && agentLoadingNodeIds.length === 0)) {
      stopPolling();
      return;
    }

    // 获取容器列表
    // - 初始轮询：获取所有容器
    // - 后续轮询：只获取正在处理的容器
    const { containers, canvas_id } = await fetchContainerListByIds(
      agentCanvasDetail.id,
      type !== 'startPolling' ? agentLoadingNodeIds : [],
    );

    // 检查画布 ID 是否匹配（防止切换画布后继续轮询）
    if (canvas_id !== agentCanvasDetail.id) {
      stopPolling();
      return;
    }

    const newContainers = [];
    const clipContainers = [];

    // 如果节点是分镜节点
    for (const container of containers) {
      if (container.type === 'clip') {
        clipContainers.push(container);
      } else {
        newContainers.push(container);
      }
    }

    // 增量更新节点
    const newContainerIds = batchUpdateAgentContainers(newContainers.reverse());

    // 如果分镜节点存在，则设置分镜脚本中loading的节点列表
    if (clipContainers.length > 0) {
      setAgentEditInClipLoadingNodes(clipContainers.map((container) => convertContainerToNodeData(container)));
    }

    // 筛选出仍在处理中的节点
    const stillProcessingNodeIds = containers
      .filter(
        (container) =>
          isContainerProcessing(container.status as ContainerStatus) &&
          container.id &&
          (container.parent_ids || []).length > 0 &&
          !(container.type === 'clip' && !agentIsOpenEditInClipDialog),
      )
      .map((container) => container.id || '');

    // 更新正在加载中的节点 ID
    setAgentLoadingNodeIds(stillProcessingNodeIds);

    // 如果有新节点，自动定位到最后一个
    if (newContainerIds.length > 0) {
      const lastNodeId = newContainerIds[newContainerIds.length - 1];
      // 延迟执行，确保节点已渲染
      setTimeout(() => {
        focusNode(lastNodeId);
      }, 100);
    }

    // 如果还有正在处理中的任务，继续轮询
    if (stillProcessingNodeIds.length > 0) {
      const timer = setTimeout(() => {
        checkAndUpdateContainers();
      }, POLLING_INTERVAL);

      // 更新 store 中的 timer
      setAgentPollingState(true, timer);
    } else {
      // 所有任务完成，停止轮询
      stopPolling();
    }
  };

  /**
   * 开始轮询任务
   *
   * @param type - 轮询类型，'startPolling' 表示初始轮询
   * @param delay - 延迟开始时间（毫秒），默认 500ms
   */
  const startPolling: StartPollingFunction = (type?: PollingType, delay = DEFAULT_POLLING_DELAY) => {
    updateCredits();
    // 如果已经在轮询中，不重复启动（除非是初始轮询）
    if (agentIsPolling && type !== 'startPolling') {
      return;
    }

    // 清除旧的 timer（如果有）
    clearAgentPollingTimer();

    // 延迟后开始轮询
    const timer = setTimeout(() => {
      checkAndUpdateContainers(type);
    }, delay);

    setAgentPollingState(true, timer);
  };

  return {
    startPolling,
    stopPolling,
  };
};
